/** Inline snippet builders for analytics tags (pure, no I/O). */

/** Consent Mode v2 defaults — MUST run before GTM/GA. Defines dataLayer/gtag and denies non-essential storage. */
export function consentDefaultScript(): string {
  return `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
    `gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',wait_for_update:500});`;
}

export function ga4ConfigScript(id: string): string {
  return `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
    `gtag('js',new Date());gtag('config','${id}');`;
}

export function gtmHeadScript(id: string): string {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
    `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;` +
    `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
}

export function gtmBodyNoscript(id: string): string {
  return `<iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
}

export function fbPixelScript(id: string): string {
  return `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};` +
    `if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
    `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
    `fbq('init', '${id}');fbq('track', 'PageView');`;
}

export function fbPixelNoscript(id: string): string {
  return `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/>`;
}

/**
 * Load Microsoft Clarity from a CLIENT effect (AFTER hydration) — never via a
 * next/script that executes during React's commit phase. Running Clarity's loader
 * + tag during hydration threw "a[c] is not a function" from Clarity's tag, and an
 * uncaught throw mid-commit takes hydration down with it (React #418) — even with
 * no ad-blocker. This does the same two things the official snippet does (seed the
 * command queue, insert the tag) but from a plain effect, idempotently, and never
 * throws.
 */
export function loadClarity(id: string): void {
  try {
    const w = window as unknown as { clarity?: unknown };
    if (typeof w.clarity !== 'function') {
      if (w.clarity != null) return; // a blocker/foreign value owns window.clarity — leave it
      const stub = Object.assign(
        (...args: unknown[]) => { stub.q.push(args); },
        { q: [] as unknown[][] },
      );
      w.clarity = stub;
    }
    if (document.getElementById('clarity-js')) return; // tag already inserted — don't double-load
    const s = document.createElement('script');
    s.id = 'clarity-js';
    s.async = true;
    s.src = `https://www.clarity.ms/tag/${id}`;
    document.head.appendChild(s);
  } catch { /* analytics must never crash the page */ }
}

/**
 * Clarity ConsentV2 signal — REQUIRED for EEA/UK/CH visitors (enforced by Clarity
 * since Oct 2025): without it the tag is served with "track":false and records
 * nothing for those regions, even after our own banner granted consent.
 * Queue-safe: seeds the exact command-queue stub the official snippet uses
 * (pushes `arguments`, shares `.q`), so the call survives any load order.
 */
export function signalClarityConsentV2(
  w: { clarity?: unknown },
  analytics: boolean,
  marketing: boolean,
): void {
  // Defensive on purpose: ad-blockers pre-define window.clarity as a NON-function
  // shim, which made the naive `w.clarity || stub` pattern throw and (uncaught in
  // an effect) white-screen the whole app. An analytics signal must never be able
  // to break the page — bail on foreign values, swallow any throw.
  try {
    let clarity = w.clarity;
    if (typeof clarity !== 'function') {
      if (clarity != null) return; // blocker shim owns window.clarity — skip silently
      const stub = Object.assign(
        (...args: unknown[]) => { stub.q.push(args); },
        { q: [] as unknown[][] },
      );
      w.clarity = stub;
      clarity = stub;
    }
    (clarity as (...args: unknown[]) => void)('consentv2', {
      ad_Storage: marketing ? 'granted' : 'denied',
      analytics_Storage: analytics ? 'granted' : 'denied',
    });
  } catch { /* never let a consent signal crash the site */ }
}
